"use client";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { FaGithub } from "react-icons/fa6";

interface Metrics {
  github?: number;
  warpbuild?: number;
  // depot?: number;
  // blacksmith?: number;
  [key: string]: number | undefined;
}

interface BenchmarkCardProps {
  orgName: string;
  repoName?: string;
  repoLogo: string;
  workflowUrl: string;
  // runsUrl?: string;
  speedup: string;
  metrics: Metrics;
  maxTime?: number;
}

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

export function BenchmarkCard({
  orgName,
  // repoName,
  repoLogo,
  workflowUrl,
  // runsUrl,
  speedup,
  metrics,
}: BenchmarkCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Define the providers we want to display if they exist
  const providerConfigs = [
    {
      key: "warpbuild",
      name: "WarpBuild",
      logo: "/images/warp-logo.svg",
      barColor: "bg-primary",
    },
    {
      key: "github",
      name: "GitHub",
      icon: FaGithub,
      barColor: "bg-muted",
    },
    // {
    //   key: "depot",
    //   name: "Depot",
    //   logo: "/images/depot-logo.svg",
    //   barColor: "bg-muted",
    // },
    // {
    //   key: "blacksmith",
    //   name: "Blacksmith",
    //   logo: "/images/blacksmith-logo.svg",
    //   barColor: "bg-muted",
    // },
  ];

  // Filter providers to only include those with values in the metrics
  const providers = providerConfigs
    .filter(
      (provider) =>
        metrics[provider.key] !== undefined && metrics[provider.key] !== null
    )
    .map((provider) => ({
      ...provider,
      time: metrics[provider.key] as number,
    }));

  // Calculate the local maximum time based on the available metrics
  const localMaxTime = Math.max(...providers.map((provider) => provider.time));

  return (
    <div
      ref={cardRef}
      className="bg-surface-default border border-border-default rounded-lg p-4 transition-all duration-300"
    >
      <div className="flex justify-between items-center pb-3 mb-3 border-b border-dashed border-border-hover">
        <div className="flex items-center">
          <Image
            src={repoLogo || "/images/warp-logo.svg"}
            alt={orgName}
            width={24}
            height={24}
            className="mr-4"
          />
          <div className="flex flex-col">
            <h3 className="text-base text-text-body-subtle hover:text-text-body-primary hover:underline underline-offset-4">
              {/* <span>{orgName}</span> */}
              <a href={workflowUrl} target="_blank" rel="noopener noreferrer">
                {orgName}
              </a>
              {/* <span className="text-text-body-secondary font-medium mx-1.5">
                .
              </span>
              <a
                href={runsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-body-secondary hover:text-text-body-subtle hover:underline underline-offset-4"
              >
                recent runs
              </a> */}
            </h3>
          </div>
        </div>
        <div className="text-lg text-text-body-primary font-medium">
          <span className="text-text-body-green mr-2">{speedup}</span>
          Faster
        </div>
      </div>
      <div className="space-y-1">
        {providers.map((provider) => (
          <div
            key={provider.name}
            className="grid grid-cols-[48px_1fr_auto] items-center gap-1"
          >
            {provider.icon ? (
              <provider.icon className="w-5 h-5 text-icon-white ml-2" />
            ) : (
              <Image
                src={provider.logo! || "/images/warp-logo.svg"}
                alt={provider.name}
                width={20}
                height={20}
                className="ml-2"
              />
            )}
            <div
              className={`h-4 ${provider.barColor} rounded-[2px] transition-all duration-400 ease-out`}
              style={{
                width: isVisible
                  ? `${(provider.time / localMaxTime) * 100}%`
                  : "0%",
              }}
            />
            <div className={`inline-flex py-1 px-1.5 rounded-sm ml-4`}>
              <span className={`text-sm text-text-body-secondary`}>
                {formatTime(provider.time)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
